import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  PromptGroup,
  ToneType,
  VoiceGroupFactor,
  VoiceTagType,
} from 'service/list'
import classNames from 'classnames'
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { FormValues, PointModalData } from 'utils/type'

import { PlanTag } from 'components/planTag'
import { Me } from 'service/user'
import { isMobile } from 'react-device-detect'
import { TypeCastVoicePlayer } from '../voice/TypeCastVoicePlayer'
import { SelectionModal } from 'components/layout/SelectionModal'
import { VoiceCloningModal } from 'components/VoiceCloningModal'
import { UpdateTag } from 'components/tag/UpdateTag'
import { UserVoiceDiv } from 'components/VoiceCloningModal/components/UserVoiceDiv'
import useGetVoiceCloningTicket from 'hooks/query/useGetVoiceCloningTicket'

import { VoiceCloningWait } from 'components/popup/VoiceCloningWait'
import { VoiceUpdateTag } from 'components/tag/VoiceUpdateTag'
import { useNavigate } from 'react-router-dom'
import CashUseModalAllType from 'components/CashUseModalAllType'
import { useGetMyPoint } from 'hooks/query/point/useGetMyPoint'
import {
  DISCOUNT_VOICE_TICKET_AMOUNT,
  ORIGINAL_VOICE_TICKET_AMOUNT,
} from 'utils/const'
import { UserType } from 'utils/common'

interface AiScriptDisplayProps {
  watch: UseFormWatch<FormValues>
  setValue: UseFormSetValue<FormValues>
  control: Control<FormValues, any>
  translatePlanName: (planName: string) => string
  promptGroup: PromptGroup[] | undefined
  voiceList: VoiceGroupFactor[] | undefined
  userVoiceList: VoiceGroupFactor[] | undefined
  userInfo: Me
  setPromptStatus: Dispatch<SetStateAction<boolean[]>>
  setVoicePlaying: Dispatch<SetStateAction<boolean[]>>
  setToneTooltip: Dispatch<SetStateAction<boolean[]>>
  promptStatus: boolean[]
  voicePlaying: boolean[]
  toneTooltip: boolean[]
  voiceTags: VoiceTagType | undefined
  userVoiceRefetch: any
}

export const AiScriptDisplay = ({
  watch,
  setValue,
  control,
  translatePlanName,
  userInfo,
  setVoicePlaying,
  voicePlaying,
  voiceList,
  userVoiceList,
  voiceTags,
  userVoiceRefetch,
}: PropsWithChildren<AiScriptDisplayProps>) => {
  const { data, refetch: ticketRefetch } = useGetVoiceCloningTicket()
  const { data: myPointData, refetch: getMyPointRefetch } = useGetMyPoint()
  const navigate = useNavigate()
  const voiceRefs = useRef<(HTMLAudioElement | null)[]>([])

  const handleCloningVoiceTicket = () => {
    if (data) {
      setVoiceCloningModalOpen((prev) => !prev)
    } else {
      setCashModalOpen(true)
    }
  }

  const [pointData, setPointData] = useState<PointModalData>({
    usageList: [
      {
        label: '내 목소리 만들기',
        amount: 5000,
      },
    ],
    totalAmount: 5000,
  })

  const [isVoiceCloningModalOpen, setVoiceCloningModalOpen] =
    useState<boolean>(false)
  const [isVoiceCloningWaitModalOpen, setVoiceCloningWaitModalOpen] =
    useState<boolean>(false)
  const [isCashModalOpen, setCashModalOpen] = useState<boolean>(false)
  const [isRequest, setRequest] = useState<boolean>(true)
  const [saveSuccessModal, setSaveSuccessModal] = useState<boolean>(false)
  const [isModalOpen, setModalOpen] = useState<boolean>(false)
  const [isGenderSelectBoxOpen, setGenderSelectBoxOpen] =
    useState<boolean>(false)
  const [isAgeSelectBoxOpen, setAgeSelectBoxOpen] = useState<boolean>(false)

  const [genderWords, setGenderWords] = useState<string[]>(
    voiceTags?.genders ?? ['남성', '여성'],
  )

  const [ageWords, setAgeWords] = useState<string[]>(
    voiceTags?.ages ?? ['청년', '중년 이상', '청소년'],
  )

  const [voiceName, setVoiceName] = useState<string>('')
  const [genderValue, setGenderValue] = useState<string>('')
  const [ageValue, setAgeValue] = useState<string>('')
  const [voiceListValue, setVoiceListValue] = useState<VoiceGroupFactor[] | []>(
    [],
  )

  const handleListModalOpen = () => {
    setModalOpen((prev) => !prev)
  }

  const handleVoicePlayButtonClick = (status: boolean, index: number) => {
    const updatedPlayingState = voicePlaying.map((value, idx) =>
      idx === index ? status : false,
    )

    setVoicePlaying(updatedPlayingState)
    if (voiceRefs.current[index]) {
      if (status) {
        voiceRefs.current[index]?.play()
      } else {
        voiceRefs.current[index]?.pause()
      }
    }
  }
  const handleGenderChange = (gender: string) => {
    setGenderWords(
      (prev) =>
        prev.includes(gender)
          ? prev.filter((item) => item !== gender) // 체크 해제
          : [...prev, gender], // 체크 선택
    )
  }
  const handleAgeChange = (age: string) => {
    setAgeWords(
      (prev) =>
        prev.includes(age)
          ? prev.filter((item) => item !== age) // 체크 해제
          : [...prev, age], // 체크 선택
    )
  }

  useEffect(() => {
    if (voiceList && watch('voiceId') !== 0) {
      //voice아이디를 가지고 list에서 찾아서 gender와 age를 셋팅해야함
      const selectedVoiceId = watch('voiceId')
      const [voice] = voiceList.filter((el) => el.id === selectedVoiceId)
      if (voice) {
        setVoiceName(voice?.name)
        setAgeValue(voice.age ?? '')
        setGenderValue(voice.gender ?? '')
      }
    }

    if (userVoiceList && watch('userVoiceId') !== 0) {
      const selectedVoiceId = watch('userVoiceId')
      const [voice] = userVoiceList.filter((el) => el.id === selectedVoiceId)

      if (voice) {
        setVoiceName(voice?.name)
        setAgeValue(voice.age ?? '')
        setGenderValue(voice.gender ?? '')
      }
    }
  }, [voiceList, userVoiceList, watch('voiceId'), watch('userVoiceId')])

  useEffect(() => {
    voiceRefs.current.forEach((audioRef, index) => {
      if (audioRef && !voicePlaying[index]) {
        audioRef.pause()
        audioRef.currentTime = 0
      }
    })
  }, [voicePlaying])

  useEffect(() => {
    if (voiceList) {
      let filter = voiceList

      // 1차: 성별 필터링
      if (genderWords.length > 0) {
        filter = filter.filter((el) => genderWords.includes(el?.gender ?? ''))
      } else {
        filter = []
      }

      // 2차: 연령 필터링
      if (ageWords.length > 0) {
        filter = filter.filter((el) => ageWords.includes(el.age ?? ''))
      } else {
        filter = []
      }

      // 상태 업데이트
      const prevVoiceId = watch('voiceId')

      setVoiceListValue(filter)
      setVoicePlaying(new Array(filter.length).fill(false))

      // voiceId 설정 (사용 가능한 첫 번째 항목 또는 이전 ID 유지)

      // setValue('voiceId', filter[0]?.usable ? filter[0].id : prevVoiceId)
    }
  }, [genderWords, ageWords, voiceList])

  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add('overflow-hidden') // Tailwind 유틸리티 클래스 사용
    } else {
      document.body.classList.remove('overflow-hidden')
    }
  }, [isModalOpen])

  return (
    <>
      <div className="bgm mb-10">
        <div className="flex flex-col justify-between md:flex-row md:items-center">
          <p className="mb-4 text-center text-[18px] font-bold md:mb-0">
            {' '}
            AI 생성 <br />
            목소리
          </p>
          <p
            className={classNames('', {
              'flex h-[61px] w-[630px] items-center justify-between rounded-[10px] bg-[#F6F8FB] px-4 py-5 font-bold':
                !isMobile,
              'flex h-[80px] w-full items-center justify-between rounded-[10px] bg-[#F6F8FB] px-4 py-5 font-bold':
                isMobile,
            })}
          >
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-5">
              <p className="flex items-center gap-1 text-[14px]">
                <span className="flex-shrink-0 rounded-[4px] bg-[#D9DEE8] px-1 py-[2px] text-[#8F929B]">
                  목소리
                </span>
                <div className="flex items-center">
                  <span
                    className="rounded-[4px] px-1 py-[2px] text-[11px] font-bold text-white"
                    style={{
                      background: `linear-gradient(to right,#6095FF,#00CBB3)`,
                    }}
                  >
                    UPDATE
                  </span>
                </div>
                <span className="flex-shrink-0">{voiceName}</span>
              </p>
              {isMobile && <VoiceUpdateTag />}
            </div>

            <div
              className={classNames('', {
                'flex items-center': !isMobile,
                'flex flex-col items-center gap-1': isMobile,
              })}
            >
              {!isMobile && <VoiceUpdateTag />} 
              <button
                className="rounded-[4px] bg-[#2E343D] px-2 py-[6px] text-[14px] text-[white]"
                onClick={handleListModalOpen}
                type="button"
              >
                선택 {'>'}
              </button>
            </div>
          </p>
        </div>
      </div>

      <SelectionModal isModal={isModalOpen}>
        <div
          className={classNames('', {
            'flex h-auto w-[816px] flex-col gap-8 bg-white px-8 pb-44 pt-8':
              !isMobile,
            'flex w-full flex-col gap-8 overflow-auto bg-white px-4 pt-10':
              isMobile,
          })}
        >
          <div className="flex flex-col gap-4 rounded-[6px] border-[1px] bg-[#F1F8FF] p-4">
            <div className="flex flex-col items-center gap-3 md:items-start">
              <div className="flex items-start gap-2">
                <p className="text-left text-[24px] font-bold">AI 내 목소리</p>
                <UpdateTag />
              </div>
              <span>10문장만 녹음하면 내 목소리를 만들어드려요!</span>
              <p className="flex items-center gap-1">
                <img
                  src="assets/img/cash.png"
                  alt=""
                  className="h-[13px] w-[11px]"
                />
                <s className="text-[#5E6068]">
                  {ORIGINAL_VOICE_TICKET_AMOUNT.toLocaleString()}
                </s>
                <span className="font-[500] text-main">
                  {DISCOUNT_VOICE_TICKET_AMOUNT.toLocaleString()} (최초 1회 무료
                  제작)
                </span>
              </p>
            </div>

            <button
              className="flex h-[54px] w-[240px] items-center gap-2 rounded-[4px] border-[1px] border-[#D9DEE8] bg-[white] px-4 disabled:bg-gray-100 disabled:text-gray-400"
              type="button"
              onClick={handleCloningVoiceTicket}
              disabled={
                !(
                  userInfo?.userType === UserType.프리미엄 ||
                  userInfo?.userType === UserType['숏폼 스페셜']
                )
              }
            >
              <div className="flex flex-col justify-end gap-1">
                <PlanTag planName="PREMIUM" />
                <PlanTag planName="SPECIAL" />
              </div>
              <span>{`🗣️ 내 목소리 만들기 >`}</span>
            </button>

            {userInfo?.subscriptions?.planId === 3 ||
            userInfo?.subscriptions?.planId === 5 ? (
              <>
                {' '}
                <div className="grid grid-cols-1 gap-x-1 gap-y-2 md:grid-cols-2 md:pb-4">
                  {userVoiceList?.map((voice, index) => {
                    return (
                      <Controller
                        key={voice.id || index} // 반드시 key 추가!
                        render={({ field }) => (
                          <UserVoiceDiv
                            {...field}
                            id={index}
                            index={index}
                            setVoiceName={setVoiceName}
                            setValue={setValue}
                            watch={watch}
                            voice={voice}
                            planName={translatePlanName(voice?.planName)}
                            userPlan={userInfo?.subscriptions?.planName}
                            userVoiceRefetch={userVoiceRefetch}
                          />
                        )}
                        name="userVoiceId"
                        control={control}
                      />
                    )
                  })}
                </div>
              </>
            ) : (
              <>
                <div
                  className="flex cursor-pointer items-center gap-3"
                  onClick={() => {
                    navigate('/price-guide')
                  }}
                >
                  <p>
                    내 목소리를 생성하고 사용하려면{' '}
                    <span className="font-bold">{"'프리미엄'"}</span>
                    플랜이나{' '}
                    <span className="font-bold">{"'숏폼 스페셜'"}</span>{' '}
                    플랜으로 업그레이드 해보세요 {`>>`}
                  </p>
                </div>
              </>
            )}
          </div>
          <div className="flex flex-col gap-4">
            <p className="flex flex-col items-center gap-4 md:items-start">
              <span className="text-left text-[24px] font-bold">
                AI 기본 목소리
              </span>
              <span>대본 컨셉에 맞춰 어울리는 목소리를 선택해보세요!</span>
            </p>
          </div>
          <div className="flex flex-col gap-8 pb-40 md:pb-0">
            <div className="ai-prompt flex flex-col items-start md:min-h-[300px]">
              <div
                className={classNames('', {
                  'select_box mb-[16px] flex gap-4': !isMobile,
                  'select_box mb-[16px] flex flex-col gap-4': isMobile,
                })}
              >
                <div className="flex items-center gap-2">
                  <span>성별</span>{' '}
                  <div
                    className="relative"
                    onMouseLeave={() => {
                      setGenderSelectBoxOpen(false)
                    }}
                  >
                    <button
                      className={classNames('', {
                        'h-[33px] w-[140px] rounded-[6px] border-[1px] border-[#D4D9DF] px-4 py-1 text-left text-[14px] font-[500]':
                          !isMobile,
                        'h-[51px] w-[140px] rounded-[6px] border-[1px] border-[#D4D9DF] px-4 py-1 text-left font-[500]':
                          isMobile,
                      })}
                      value={genderValue}
                      type="button"
                      onClick={() => {
                        setGenderSelectBoxOpen((prev) => !prev)
                      }}
                    >
                      {' '}
                      {genderWords.length > 0 ? genderWords.join(', ') : '성별'}
                    </button>
                    <img
                      src="assets/img/chevron-down.png"
                      className={classNames('', {
                        'absolute bottom-2 right-2 h-[16px] w-[16px]':
                          !isMobile,
                        'absolute bottom-4 right-2 h-[16px] w-[16px]': isMobile,
                      })}
                      alt=""
                      onClick={() => {
                        setGenderSelectBoxOpen((prev) => !prev)
                      }}
                    />
                    {isGenderSelectBoxOpen && (
                      <div className="absolute z-50 flex h-[100px] w-[120px] flex-col justify-center gap-2 rounded-[10px] bg-white px-3 py-1 shadow-main">
                        {voiceTags?.genders.map((gender, index) => {
                          return (
                            <>
                              <div className="flex items-center gap-2">
                                <input
                                  id={`gender-${index}`} // 고유한 id
                                  type="checkbox"
                                  className="h-[20px] w-[20px]"
                                  value={gender}
                                  checked={genderWords.includes(gender)}
                                  onChange={() => handleGenderChange(gender)}
                                />
                                <label htmlFor={`gender-${index}`}>
                                  {gender}
                                </label>
                              </div>
                            </>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div
                  className={classNames('flex items-center gap-2', {
                    '': !isMobile,
                  })}
                >
                  <span>연령</span>{' '}
                  <div
                    className="relative"
                    onMouseLeave={() => {
                      setAgeSelectBoxOpen(false)
                    }}
                  >
                    <button
                      className={classNames('', {
                        'h-[33px] w-[179px] rounded-[6px] border-[1px] border-[#D4D9DF] px-4 py-1 text-left text-[14px] font-[500]':
                          !isMobile,
                        'h-[51px] w-[197px] rounded-[6px] border-[1px] border-[#D4D9DF] px-4 py-1 text-left font-[500]':
                          isMobile,
                      })}
                      value={ageValue}
                      type="button"
                      onClick={() => {
                        setAgeSelectBoxOpen((prev) => !prev)
                      }}
                    >
                      {' '}
                      {ageWords.length > 0 ? ageWords.join(', ') : '연령'}
                    </button>
                    <img
                      src="assets/img/chevron-down.png"
                      className={classNames('', {
                        'absolute bottom-2 right-2 h-[16px] w-[16px]':
                          !isMobile,
                        'absolute bottom-4 right-2 h-[16px] w-[16px]': isMobile,
                      })}
                      alt=""
                      onClick={() => {
                        setAgeSelectBoxOpen((prev) => !prev)
                      }}
                    />{' '}
                    {isAgeSelectBoxOpen && (
                      <div className="absolute right-0 z-50 flex h-[140px] w-[120px] flex-col justify-center gap-2 rounded-[10px] bg-white px-3 py-1 shadow-main">
                        {voiceTags?.ages.map((age, index) => {
                          return (
                            <label className="flex w-full items-center justify-start gap-2">
                              <input
                                id={`age-${index}`} // 고유한 id
                                type="checkbox"
                                className="h-[20px] w-[20px]"
                                value={age}
                                checked={ageWords.includes(age)}
                                onChange={() => handleAgeChange(age)}
                              />
                              <span>{age}</span>
                            </label>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-1 gap-y-2 md:grid-cols-3 md:pb-4">
                {voiceListValue?.map((voice, index) => {
                  return (
                    <Controller
                      render={({ field }) => (
                        <TypeCastVoicePlayer
                          {...field}
                          id={index}
                          index={index}
                          playing={voicePlaying}
                          handleFn={handleVoicePlayButtonClick}
                          checked={watch('voiceId') === voice.id}
                          disabled={voice?.usable === 0}
                          onChange={() => {
                            setValue('voiceId', voice.id)
                            setValue('userVoiceId', 0)
                            setVoiceName(voice.name)
                          }}
                          label={voice.name}
                          planName={translatePlanName(voice?.planName)}
                          voiceRefs={voiceRefs}
                          src={voice.path}
                          imagePath={voice.image ?? ''}
                          service={voice?.service}
                          userPlan={userInfo?.subscriptions?.planName}
                        />
                      )}
                      name="voiceId"
                      control={control}
                    />
                  )
                })}
              </div>
            </div>
            {isMobile && (
              <div
                className={classNames(
                  'z-50 flex w-[335px] flex-col gap-8 bg-white',
                  {},
                )}
              >
                <button
                  className="z-50 h-[60px] w-full rounded-full bg-main font-bold text-white"
                  onClick={handleListModalOpen}
                  type="button"
                >
                  저장
                </button>
              </div>
            )}
          </div>
        </div>
        {!isMobile && (
          <div className="sticky bottom-0 z-50 w-full rounded-b-[20px] bg-white p-8 shadow-main before:absolute before:left-0 before:top-[-10px] before:h-[10px] before:w-full before:bg-gradient-to-t before:from-black/10 before:to-transparent before:content-[''] md:w-[816px]">
            <button
              className="z-50 h-[60px] w-full rounded-full bg-main font-bold text-white"
              onClick={handleListModalOpen}
              type="button"
            >
              저장
            </button>
          </div>
        )}
        {isVoiceCloningModalOpen && (
          <VoiceCloningModal
            isModalOpen={isVoiceCloningModalOpen}
            setModalOpen={setVoiceCloningModalOpen}
            setVoiceCloningWaitModalOpen={setVoiceCloningWaitModalOpen}
            userVoiceRefetch={userVoiceRefetch}
            ticketRefetch={ticketRefetch}
          />
        )}

        {isVoiceCloningWaitModalOpen && (
          <VoiceCloningWait
            isModalOpen={isVoiceCloningWaitModalOpen}
            setIsModalOpen={setVoiceCloningWaitModalOpen}
            userVoiceRefetch={userVoiceRefetch}
          />
        )}

        {isCashModalOpen && (
          <CashUseModalAllType
            isUseModal={isCashModalOpen}
            setUseModal={setCashModalOpen}
            setVoiceCloningModalOpen={setVoiceCloningModalOpen}
            currentPoint={myPointData.totalPoint}
            pointData={pointData}
            setSaveSuccessModal={setSaveSuccessModal}
            getMyPointRefetch={getMyPointRefetch}
            setRequest={setRequest}
            ticketRefetch={ticketRefetch}
          />
        )}
      </SelectionModal>
    </>
  )
}
